import apiService from './apiService';
import { GET, POST, PUT, PATCH, DELETE } from './executer';

/**
 * Observation Field entity from the database
 * Used to configure how station observation readings are displayed in the UI
 */
export interface ObservationField {
  name: string;             // Field name - identifier and matches readings array field name
  display_name: string;     // Human-readable display name shown in UI
  unit: string | null;      // Unit of measurement (e.g., '°C', 'm/s', '%')
  position: number | null;  // Display order (lower = first, null = last)
  demo_only: boolean;       // If true, field is only available for demo events
  hidden: boolean;          // If true, field is hidden from the UI
  include_in_historical_trend: boolean;  // If true, field appears in Historical Weather Trend view
  include_in_session_compare: boolean;   // If true, field appears in Session Comparison view

  // Soft delete flag - when true, field is excluded from default queries
  deleted?: boolean;

  // Audit fields - last update info
  updated_by?: string | null;
  update_datetime?: string | null;
}

/**
 * Request payload for creating a new observation field
 */
export interface CreateObservationFieldRequest {
  name: string;
  display_name: string;
  unit?: string | null;
  position?: number | null;
  demo_only?: boolean;  // Defaults to false if not provided
  hidden?: boolean;     // Defaults to false if not provided
  include_in_historical_trend?: boolean;  // Defaults to false if not provided
  include_in_session_compare?: boolean;   // Defaults to false if not provided
}

/**
 * Request payload for updating an observation field
 */
export interface UpdateObservationFieldRequest {
  display_name?: string;
  unit?: string | null;
  position?: number | null;
  demo_only?: boolean;
  hidden?: boolean;
  include_in_historical_trend?: boolean;
  include_in_session_compare?: boolean;
}

/**
 * Request payload for batch reordering observation fields
 */
export interface ReorderObservationFieldsRequest {
  fields: Array<{
    name: string;
    position: number;
  }>;
}

/**
 * Response from the observation fields API
 */
export interface ObservationFieldsResponse {
  success: boolean;
  data: ObservationField[];
  count: number;
}

export interface ObservationFieldResponse {
  success: boolean;
  data: ObservationField;
}

class ObservationFieldsService {
  /**
   * Get all observation fields
   * @param includeDeleted - If true, includes soft-deleted fields (for management UI)
   */
  async getAllObservationFields(includeDeleted: boolean = false): Promise<ObservationField[]> {
    try {
      const url = includeDeleted ? '/api/observation-fields?include_deleted=true' : '/api/observation-fields';
      const response = await apiService.client.execute<ObservationField[]>(
        GET,
        url
      );
      
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Failed to fetch observation fields');
    } catch (error) {
      console.error('Error fetching observation fields:', error);
      throw error;
    }
  }

  /**
   * Get an observation field by name
   * @param includeDeleted - If true, includes soft-deleted fields
   */
  async getObservationFieldByName(name: string, includeDeleted: boolean = false): Promise<ObservationField> {
    try {
      const url = includeDeleted ? `/api/observation-fields/${name}?include_deleted=true` : `/api/observation-fields/${name}`;
      const response = await apiService.client.execute<ObservationField>(
        GET,
        url
      );
      
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(`Failed to fetch observation field: ${name}`);
    } catch (error) {
      console.error(`Error fetching observation field ${name}:`, error);
      throw error;
    }
  }

  /**
   * Create a new observation field
   * Requires: root role
   */
  async createObservationField(fieldData: CreateObservationFieldRequest, userToken: string) {
    return await apiService.client.execute<ObservationField>(
      POST,
      '/api/observation-fields',
      {
        data: fieldData,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Update an existing observation field
   * Requires: root role
   */
  async updateObservationField(
    name: string,
    updates: UpdateObservationFieldRequest,
    userToken: string
  ) {
    return await apiService.client.execute<ObservationField>(
      PUT,
      `/api/observation-fields/${name}`,
      {
        data: updates,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Delete an observation field (soft delete)
   * Requires: root role
   */
  async deleteObservationField(name: string, userToken: string) {
    return await apiService.client.execute<{ message: string }>(
      DELETE,
      `/api/observation-fields/${name}`,
      {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Set the soft-delete flag on an observation field (for restore or soft-delete)
   * Requires: root role
   */
  async setObservationFieldDeleted(name: string, deleted: boolean, userToken: string) {
    return await apiService.client.execute<{ success: boolean; message: string }>(
      PATCH,
      `/api/observation-fields/${name}/deleted`,
      {
        data: { deleted },
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Batch reorder observation fields
   * Requires: root role
   */
  async reorderObservationFields(
    fields: Array<{ name: string; position: number }>,
    userToken: string
  ) {
    return await apiService.client.execute<{ message: string }>(
      PUT,
      '/api/observation-fields/reorder',
      {
        data: { fields },
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }
}

export const observationFieldsService = new ObservationFieldsService();
export default observationFieldsService;
